import React from "react";
import { Badge, Button, Callout, Card, DataTable, PageHeader, Select, Stat } from "../components/DesignSystem";
import type { GuestImportDraft, ImportMapping } from "../domain/types";
import { errorMessage } from "../services/errors";
import { buildImportDraft, buildImportTemplateCsv, resolveRowsForSubmit, targetFieldsFor } from "../services/guestImportParser";
import type { RouteKey } from "../shell/PlannerShell";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState, useSubmitGuard } from "./common";

export function ImportGuestsScreen({ go }: { go: (route: RouteKey) => void }) {
  const { service, workspace, run } = usePlannerData();
  const [step, setStep] = React.useState(0);
  const [draft, setDraft] = React.useState<GuestImportDraft | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const { submitting, guardedSubmit } = useSubmitGuard();

  const upload = async (file: File) => {
    if (!workspace) return;
    setLoading(true);
    setError(null);
    try {
      const text = await file.text();
      const next = buildImportDraft(file.name, text, workspace);
      if (next.rowsFound === 0) throw new Error("No guest rows were found in this file. Check it has a header row plus at least one guest.");
      setDraft(next);
      setStep(1);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const submit = () => guardedSubmit(async () => {
    if (!draft || !workspace) return;
    setError(null);
    try {
      // Rows carry their household *name*; the server matches it to an existing household or
      // creates it once, in the same transaction as the guests (docs/DECISIONS.md L-025). Nothing
      // is created here first, so a failed import leaves nothing behind to duplicate on retry.
      const result = await run(() => service.invitations.submitGuestImport({ ...draft, rows: resolveRowsForSubmit(draft) }));
      setSuccess(`${result.importedPeople} guest${result.importedPeople === 1 ? "" : "s"} imported into ${result.householdsTouched} household${result.householdsTouched === 1 ? "" : "s"}.`);
      setStep(4);
    } catch (err) {
      setError(errorMessage(err));
    }
  });

  if (!workspace) return <div className="pob-pad"><LoadingState label="Preparing import" /></div>;

  return (
    <div>
      <PageHeader eyebrow="Guests" title="Import guests" meta={[{ icon: "file-spreadsheet", label: draft?.fileName || "No file uploaded" }, { icon: "rows-3", label: draft ? `${draft.rowsFound} rows` : "Upload first" }]} actions={<Button onClick={() => go("guests")}>Cancel import</Button>} />
      <div className="pob-pad">
        {error && <ErrorState message={error} />}
        {success && <Callout tone="success" title="Import complete">{success}</Callout>}
        <Stepper step={step} />
        {step === 0 && <UploadStep loading={loading} onUpload={upload} />}
        {draft && step === 1 && <MappingStep draft={draft} setDraft={setDraft} onBack={() => setStep(0)} onNext={() => setStep(2)} />}
        {draft && step === 2 && <ReviewStep draft={draft} onBack={() => setStep(1)} onNext={() => setStep(3)} />}
        {draft && step === 3 && <DuplicateStep draft={draft} setDraft={setDraft} onBack={() => setStep(2)} onSubmit={submit} loading={submitting} />}
        {draft && step === 4 && <FinalStep go={go} />}
      </div>
    </div>
  );
}

function Stepper({ step }: { step: number }) {
  return (
    <div style={{ display: "flex", gap: "var(--space-6)", overflowX: "auto" }}>
      {["Upload", "Mapping", "Review", "Duplicate resolution", "Final submit"].map((label, index) => (
        <span key={label} style={{ font: "var(--type-label)", color: index === step ? "var(--text-primary)" : index < step ? "var(--text-secondary)" : "var(--text-tertiary)", whiteSpace: "nowrap" }}>
          {index + 1} {label}{index < step ? " ✓" : ""}
        </span>
      ))}
    </div>
  );
}

function UploadStep({ loading, onUpload }: { loading: boolean; onUpload: (file: File) => void }) {
  const { workspace } = usePlannerData();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = React.useState(false);

  const handleFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (file) onUpload(file);
  };

  const downloadTemplate = () => {
    if (!workspace) return;
    const csv = buildImportTemplateCsv(workspace.events);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "guest-import-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Card eyebrow="Step 1" title="Upload" padding="var(--space-5)">
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <div
          onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(event) => { event.preventDefault(); setDragOver(false); handleFiles(event.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? "var(--accent-solid, var(--text-primary))" : "var(--border-subtle)"}`,
            borderRadius: "var(--radius-md)",
            padding: "var(--space-8)",
            textAlign: "center",
            cursor: "pointer",
            background: dragOver ? "var(--surface-sunken)" : "transparent"
          }}
        >
          <input ref={inputRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={(event) => handleFiles(event.target.files)} />
          <p style={{ font: "var(--type-label)", margin: 0 }}>{loading ? "Reading file…" : "Drop a CSV file here, or click to choose one"}</p>
          <p style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)", marginTop: 4 }}>First row must be column headers. Nothing is written until final submit.</p>
        </div>
        <Callout tone="neutral" title="No rows are written yet" action={<Button size="sm" onClick={downloadTemplate}>Download template</Button>}>
          Columns are auto-matched by header name (Name, Household, Phone, Email, one column per celebration, Dietary requirement) — you can fix any mapping on the next step.
        </Callout>
      </div>
    </Card>
  );
}

function MappingStep({ draft, setDraft, onBack, onNext }: { draft: GuestImportDraft; setDraft: (draft: GuestImportDraft) => void; onBack: () => void; onNext: () => void }) {
  const { workspace } = usePlannerData();
  const targetFields = targetFieldsFor(workspace?.events || []);
  const updateMapping = (index: number, targetField: string) => {
    const mappings: ImportMapping[] = draft.mappings.map((mapping, i) => i === index ? { ...mapping, targetField } : mapping);
    setDraft({ ...draft, mappings });
  };
  return (
    <>
      <Card eyebrow="Step 2" title="Column mapping" padding="0">
        <DataTable dense rows={draft.mappings.map((mapping, index) => ({ id: String(index), index, ...mapping }))} columns={[
          { key: "sourceColumn", header: "In your file" },
          { key: "targetField", header: "Mapped to", render: (row) => <Select size="sm" value={row.targetField} onChange={(event) => updateMapping(row.index, event.target.value)} options={targetFields} /> },
          { key: "sample", header: "Sample value", muted: true }
        ]} />
      </Card>
      <Footer onBack={onBack} onNext={onNext} nextLabel="Review rows" />
    </>
  );
}

function ReviewStep({ draft, onBack, onNext }: { draft: GuestImportDraft; onBack: () => void; onNext: () => void }) {
  const resolved = Object.keys(draft.resolutions).length;
  const needsAttention = draft.issues.length - resolved;
  return (
    <>
      <div className="pob-grid-4">
        <Card padding="var(--space-5)"><Stat label="Rows found" value={draft.rowsFound} /></Card>
        <Card padding="var(--space-5)"><Stat label="Clean rows" value={draft.rowsFound - draft.issues.length} /></Card>
        <Card padding="var(--space-5)"><Stat label="Need attention" value={needsAttention} /></Card>
        <Card padding="var(--space-5)"><Stat label="Possible duplicates" value={draft.issues.filter((issue) => issue.issue.toLowerCase().includes("match") || issue.issue.toLowerCase().includes("appears earlier")).length} /></Card>
      </div>
      <Card eyebrow="Step 3" title="Review summary">
        <Callout tone="neutral" title="Manual duplicate review">Automatic fuzzy dedupe is not enabled. Resolve flagged rows explicitly before final submit.</Callout>
      </Card>
      <Footer onBack={onBack} onNext={onNext} nextLabel="Resolve duplicates" />
    </>
  );
}

function DuplicateStep({ draft, setDraft, onBack, onSubmit, loading }: { draft: GuestImportDraft; setDraft: (draft: GuestImportDraft) => void; onBack: () => void; onSubmit: () => void; loading: boolean }) {
  const unresolved = draft.issues.filter((issue) => !draft.resolutions[issue.id]);
  return (
    <>
      <Card eyebrow="Step 4" title="Duplicate resolution" padding="0">
        <DataTable rows={draft.issues.map((issue) => ({ ...issue, resolution: draft.resolutions[issue.id] }))} emptyLabel="Every row is resolved." columns={[
          { key: "row", header: "Row", width: 70 },
          { key: "value", header: "Value" },
          { key: "issue", header: "Issue", muted: true, wrap: true },
          { key: "resolution", header: "Resolution", width: 340, render: (row) => row.resolution ? <Badge tone="success">{row.resolution}</Badge> : <span style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>{row.options.map((option) => <Button key={option} size="sm" onClick={() => setDraft({ ...draft, resolutions: { ...draft.resolutions, [row.id]: option } })}>{option}</Button>)}</span> }
        ]} />
      </Card>
      <Callout tone="neutral" title="Nothing writes before final submit">The final button calls `submitGuestImport` on the data service and then refreshes the workspace.</Callout>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--space-2)" }}>
        <Button onClick={onBack} disabled={loading}>Back</Button>
        <Button variant="primary" disabled={unresolved.length > 0 || loading} onClick={onSubmit}>{loading ? "Submitting…" : `Import ${draft.rowsFound - draft.issues.filter((issue) => draft.resolutions[issue.id] === "Skip this row").length} guests`}</Button>
      </div>
    </>
  );
}

function FinalStep({ go }: { go: (route: RouteKey) => void }) {
  return (
    <Card eyebrow="Step 5" title="Final submit">
      <Callout tone="success" title="Guest import wrote through the service">Imported rows are visible in Guests and Households without direct fixture access.</Callout>
      <div style={{ marginTop: "var(--space-4)" }}><Button variant="primary" onClick={() => go("guests")}>Open guest list</Button></div>
    </Card>
  );
}

function Footer({ onBack, onNext, nextLabel }: { onBack: () => void; onNext: () => void; nextLabel: string }) {
  return <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--space-2)" }}><Button onClick={onBack}>Back</Button><Button variant="primary" onClick={onNext}>{nextLabel}</Button></div>;
}
